import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  CalendarDays, Heart, Trash2, Sparkles, MapPin, Clock, User as UserIcon, Plus, Repeat,
} from "lucide-react";

const statusColor = {
  pending: "bg-[#F4E9C9] text-[#8A6D1E]",
  confirmed: "bg-[#D9EAD9] text-[#3B7A57]",
  completed: "bg-[#E2E8E4] text-[#4A5D4E]",
  cancelled: "bg-[#F5DAD3] text-[#C0492B]",
};

export default function Portal() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "", address: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  const load = () => {
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/visualizer/designs").then((r) => setDesigns(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (user) setProfile({ name: user.name || "", phone: user.phone || "", address: user.address || "" });
  }, [user]);

  const cancelBooking = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking cancelled.");
      load();
    } catch { toast.error("Could not cancel."); }
  };

  const toggleFav = async (id) => {
    try {
      await api.patch(`/visualizer/designs/${id}/favorite`);
      load();
    } catch { toast.error("Could not update."); }
  };

  const deleteDesign = async (id) => {
    try {
      await api.delete(`/visualizer/designs/${id}`);
      toast.success("Design removed.");
      load();
    } catch { toast.error("Could not delete."); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await api.patch("/profile", profile);
      setUser(res.data);
      toast.success("Profile updated.");
    } catch { toast.error("Could not save profile."); }
    finally { setSavingProfile(false); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.date >= today && b.status !== "cancelled");
  const past = bookings.filter((b) => b.date < today || b.status === "cancelled");

  const BookingCard = ({ b }) => (
    <div className="p-5 rounded-xl bg-white border border-[#E2E8E4] flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid={`booking-${b.booking_id}`}>
      <div>
        <div className="flex items-center gap-3">
          <h4 className="font-heading text-xl font-semibold text-[#1A362D]">{b.service_name}</h4>
          <Badge className={`${statusColor[b.status] || ""} border-0 capitalize`}>{b.status}</Badge>
          {b.recurring !== "one-time" && <span className="text-xs text-[#8FA38C] flex items-center gap-1"><Repeat className="h-3 w-3" /> {b.recurring}</span>}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#4A5D4E]">
          <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-[#8FA38C]" /> {b.date}</span>
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-[#8FA38C]" /> {b.time_slot}</span>
          <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#8FA38C]" /> {b.address}, {b.zip_code}</span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="font-heading text-xl font-semibold text-[#C47B57]">${b.price}</span>
        {b.status !== "cancelled" && b.status !== "completed" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid={`cancel-${b.booking_id}`} className="rounded-full border-[#E2E8E4] text-[#C0492B]">Cancel</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
                <AlertDialogDescription>This will cancel your {b.service_name} on {b.date}. You can rebook anytime.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it</AlertDialogCancel>
                <AlertDialogAction onClick={() => cancelBooking(b.booking_id)} className="bg-[#C0492B] hover:bg-[#A53D24]">Cancel booking</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <section className="bg-[#1A362D] py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA38C]">Customer Portal</span>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight">Welcome back, {user?.name?.split(" ")[0]}</h1>
          </div>
          <Button onClick={() => navigate("/booking")} className="bg-[#C47B57] hover:bg-[#D58C68] text-white rounded-full" data-testid="portal-new-booking">
            <Plus className="mr-2 h-4 w-4" /> New Booking
          </Button>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="bg-[#F2F4F2] p-1.5 rounded-full mb-8" data-testid="portal-tabs">
            <TabsTrigger value="bookings" className="rounded-full px-6 data-[state=active]:bg-white data-[state=active]:text-[#1A362D]">Bookings</TabsTrigger>
            <TabsTrigger value="designs" className="rounded-full px-6 data-[state=active]:bg-white data-[state=active]:text-[#1A362D]">Saved Designs</TabsTrigger>
            <TabsTrigger value="profile" className="rounded-full px-6 data-[state=active]:bg-white data-[state=active]:text-[#1A362D]">Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="space-y-8">
            <div>
              <h3 className="font-heading text-2xl font-semibold text-[#1A362D] mb-4">Upcoming</h3>
              {upcoming.length ? <div className="space-y-3">{upcoming.map((b) => <BookingCard key={b.booking_id} b={b} />)}</div>
                : <p className="text-[#8A9A8C] p-6 bg-white rounded-xl border border-[#E2E8E4]">No upcoming bookings. <button onClick={() => navigate("/booking")} className="text-[#C47B57] font-medium">Book a service →</button></p>}
            </div>
            {past.length > 0 && (
              <div>
                <h3 className="font-heading text-2xl font-semibold text-[#1A362D] mb-4">Past & Cancelled</h3>
                <div className="space-y-3 opacity-80">{past.map((b) => <BookingCard key={b.booking_id} b={b} />)}</div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="designs">
            {designs.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {designs.map((d) => (
                  <div key={d.design_id} className="rounded-xl bg-white border border-[#E2E8E4] overflow-hidden" data-testid={`design-${d.design_id}`}>
                    <div className="relative aspect-[4/3]">
                      <img src={d.after_image} alt="design" className="w-full h-full object-cover" />
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 text-xs uppercase tracking-widest text-[#1A362D] capitalize">{d.style}</span>
                    </div>
                    <div className="p-4 flex items-center justify-between">
                      <button onClick={() => navigate(`/booking?service=land_design&design=${d.design_id}`)} className="text-sm font-medium text-[#C47B57] flex items-center gap-1.5" data-testid={`request-${d.design_id}`}>
                        <Sparkles className="h-4 w-4" /> Request
                      </button>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => toggleFav(d.design_id)} data-testid={`fav-${d.design_id}`}>
                          <Heart className={`h-4 w-4 ${d.favorite ? "fill-[#C47B57] text-[#C47B57]" : "text-[#8A9A8C]"}`} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteDesign(d.design_id)} data-testid={`del-${d.design_id}`}>
                          <Trash2 className="h-4 w-4 text-[#8A9A8C]" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center p-12 bg-white rounded-xl border border-[#E2E8E4]">
                <Sparkles className="h-10 w-10 text-[#8FA38C] mx-auto" />
                <p className="mt-4 font-heading text-xl text-[#1A362D]">No saved designs yet</p>
                <p className="text-[#8A9A8C] mt-1">Try the AI Visualizer to reimagine your yard.</p>
                <Button onClick={() => navigate("/visualizer")} className="mt-5 bg-[#1A362D] hover:bg-[#254A3E] text-white rounded-full px-8">Open Visualizer</Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile">
            <div className="max-w-lg bg-white rounded-xl border border-[#E2E8E4] p-8">
              <h3 className="font-heading text-2xl font-semibold text-[#1A362D] flex items-center gap-2"><UserIcon className="h-5 w-5 text-[#C47B57]" /> Your details</h3>
              <div className="mt-6 space-y-5">
                <div>
                  <Label className="text-[#1A362D]">Name</Label>
                  <Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
                </div>
                <div>
                  <Label className="text-[#1A362D]">Email</Label>
                  <Input value={user?.email || ""} disabled className="mt-1.5 bg-[#F2F4F2] border-[#E2E8E4] text-[#8A9A8C]" />
                </div>
                <div>
                  <Label className="text-[#1A362D]">Phone</Label>
                  <Input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
                </div>
                <div>
                  <Label className="text-[#1A362D]">Address</Label>
                  <Input data-testid="profile-address" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
                </div>
                <Button onClick={saveProfile} disabled={savingProfile} data-testid="profile-save" className="bg-[#1A362D] hover:bg-[#254A3E] text-white rounded-full px-8">
                  {savingProfile ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
